import { useCallback } from "react";
import { addressLookup } from "src/components/Boundary/services";
import { useBoundariesMap } from "src/components/Boundary/useBoundary";
import { useEligibilityService } from "src/services/eligibility/useEligibilityService";
import {
  findFormAddressAnswer,
  findGradeAnswer,
  FormikValues,
} from "src/services/formTemplate/answer";
import {
  calculateSchoolsToRemove,
  RankedSchool,
} from "src/services/formTemplate/preRankingSection";
import * as AF from "src/types/formTemplate";

type Props = {
  formTemplateId: uuid;
  formId: uuid;
  rankedSchools: readonly RankedSchool[];
  completeQuestions: readonly AF.Question<AF.WithId>[];
  answers: FormikValues;
};

export const useFrontEndRankedSchoolsRemoval = ({
  formTemplateId,
  formId,
  rankedSchools,
  completeQuestions,
  answers,
}: Props) => {
  const boundariesMap = useBoundariesMap({ formId });
  const eligibilityService = useEligibilityService({ formTemplateId });

  // Just return a function to calculate when needed - no complex state management
  const calculateRemovals = useCallback(async (): Promise<RankedSchool[]> => {
    // If eligibility service is enabled, don't calculate
    if (eligibilityService.enabled) {
      return [];
    }

    // Check if we have required data
    if (!boundariesMap.hasData() || !window.google?.maps) {
      throw new Error("Required data not loaded");
    }

    const gradeConfigId = findGradeAnswer(completeQuestions, answers);
    const address = findFormAddressAnswer(completeQuestions, answers);
    const location =
      address?.geom?.coordinates ??
      (await addressLookup(new google.maps.Geocoder(), address));

    return calculateSchoolsToRemove(
      rankedSchools,
      completeQuestions,
      answers,
      gradeConfigId?.gradeConfigId,
      location,
      boundariesMap.data
    );
  }, [
    eligibilityService.enabled,
    boundariesMap,
    completeQuestions,
    answers,
    rankedSchools,
  ]);

  return {
    calculateRemovals,
    isReady: boundariesMap.hasData() && !!window.google?.maps,
  };
};
